import { ADD_CHAT_MESSAGE, API_CALL, SET_CHAT_MESSAGES, SET_CHAT_OUTAGE_MESSAGE, SET_CURRENT_CHAT_ID, UPDATE_CHAT, UPDATE_CHAT_TITLE, SET_LAST_UPDATED_AT, SET_UNREAD_MESSAGE_COUNT } from 'actions/actionConstants'
import type { AppAction } from 'actions/types/ActionTypes'
import { CREATE_NEW_CHAT, FETCH_CHAT_MESSAGES, FETCH_CHAT_WELCOME_MESSAGE, FETCH_CHATS, POST_MESSAGE_FEEDBACK } from 'actions/apiActionConstants'
import { ChatActions, createChat, createChatApi, getChatMessages, getChats, getChatWelcomeMessage, postFeedback, putServerChat, sendChatMessage } from 'api/supportAssistant'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { chatActions } from 'supportAssistant/constants/chatActions'
import { selectCurrentChat } from 'supportAssistant/selectors/selectCurrentChat'
import { buildChatMessagePayload } from 'supportAssistant/utils/messageUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { getOrderBookingNumbers, getOrdersWithAirportTransfers, getUpcomingOrders } from 'selectors/orderSelectors'
import { globalSocket } from 'supportAssistant/hooks/useSocket'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getLuxPlusSupportContactNumber, getLoyaltySupportContactNumber } from 'selectors/supportContactSelector'
import { isHotelOrder } from 'lib/order/orderUtils'
import { isCurrentOrCloseToCheckIn } from 'lib/order/isUpcoming'
import { fetchOffersById } from 'actions/OfferActions'
import { fetchOrderCancellationConfigs } from 'actions/OrderActions'
import getIsLoyaltyPrioritySupportNumberEnabled from 'loyalty/selectors/getIsLoyaltyPrioritySupportNumberEnabled'
import getLoyaltyAccountTier from 'loyalty/selectors/getLoyaltyAccountTier'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getSupportAssistantAtiRemovalVariation, getSupportAssistantBookingPickerVariation, getSupportAssistantNoAnswerAgentHandoffVariation, getSupportAssistantVersion } from 'selectors/featuresSelectors'
import { activateOptimizelyExperiment } from 'actions/OptimizelyActions'
import { Experiments } from 'constants/experiments'
import { isDefaultNoAnswerResponse } from 'supportAssistant/constants/noAnswerAgentHandoffTracking'
import { offersEscalation } from 'supportAssistant/constants/atiRemovalTracking'
import { v4 as uuidv4 } from 'uuid'

const runClientChatAction = (actionKey: string | undefined, payload?: App.ChatMessageMetaPayload) => {
  if (!actionKey || !payload) return

  const action = chatActions[actionKey]
  if (!action) return

  const result = action(payload)
  if (actionKey === ChatActions.RESEND_CONFIRMATION_EMAIL) {
    Promise.resolve(result).catch(() => {
      const intl = getCurrentIntl()
      showSnackbar(
        intl.formatMessage({
          id: '1ovZ1V-FC4AuXSJTMMgC',
          defaultMessage: "We couldn't resend your confirmation email. Please contact our support team for help.",
          description: 'Snackbar shown when resending a booking confirmation email from LESA fails',
        }),
        'critical',
        {
          heading: intl.formatMessage({
            id: 'JczoExq0y0lccXVWviSB',
            defaultMessage: 'Resend failed',
            description: 'Snackbar heading when resending a booking confirmation email from LESA fails',
          }),
        },
      )
    })
  }
}

export function fetchChats(upcoming: Array<App.Order>, chatDeepLink: App.ChatDeepLinks): AppAction {
  return async(dispatch, getState) => {
    const state = getState()

    const {
      geo: { currentRegionCode: regionCode, locale },
    } = state

    const customerId = getCurrentUserId(state)
    const account = state.auth.account
    const currentChat = selectCurrentChat(state)
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const assistantVersion = getSupportAssistantVersion(state)

    const upcomingHotelOrders = upcoming.filter(order => isHotelOrder(order)).map(({ id, items }) => ({
      id,
      itemCount: items.length,
      orderType: 'hotel',
    }))

    const upcomingHotelOfferIds = upcoming.flatMap(order => order.items.map(item => item.offerId))
    if (upcomingHotelOfferIds.length > 0) {
      await dispatch(fetchOffersById(upcomingHotelOfferIds))
    }

    /* Note: There is a potential race condition here. The redux thunk fetchOrderCancellationConfigs
    does not return a promise from the API call, so we cannot properly await the state update.
    The state may not be fully updated by the time we call getOrderBookingNumbers below.
    Fixing this properly requires substantial changes to the middleware to return promises from thunks. */
    const upcomingAccommodationOrderIds = upcoming
      .filter(order => isHotelOrder(order) || order.bedbankItems.length > 0)
      .map(order => order.id)
    upcomingAccommodationOrderIds.forEach(orderId => dispatch(fetchOrderCancellationConfigs(orderId)))

    // Get updated state after fetching cancellation configs
    const updatedState = getState()
    const orderBookingNumbers = getOrderBookingNumbers(updatedState)

    if (!customerId) return

    dispatch({
      type: API_CALL,
      api: FETCH_CHATS,
      request: async() => {
        // If transitioning from logged out to logged in, claim the anonymous chat.
        if (globalSocket && currentChat && !currentChat.customerId && currentChat.id !== 'default') {
          await putServerChat({ id: currentChat.id, customerId })
        }

        if (chatDeepLink === 'list-orders' && upcomingHotelOrders.length > 0) {
          const payload: App.ChatApi = {
            customerName: account.givenName,
            customerId,
            upcomingOrders: upcomingHotelOrders,
            isLuxPlusMember: canViewLuxPlusBenefits,
            chatDeepLink,
            regionCode,
            isCurrentOrder: upcoming.every(isCurrentOrCloseToCheckIn),
            orderBookingNumbers,
            locale,
            assistantVersion,
          }

          await createChatApi(payload)
        }

        const chats = await getChats()
        const activeChat = chats.find((chat) => chat.status === 'ACTIVE')
        const currentChatId = activeChat?.id ?? (chats.length > 0 ? chats[0].id : undefined)

        return {
          chats,
          currentChatId,
        }
      },
    })
  }
}

export function ensureActiveChat(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { chats, fetching } = state.supportAssistant.chats

    if (fetching) return

    const activeChat = chats.find((chat: App.Chat) => chat.status === 'ACTIVE')
    if (activeChat) return

    if (!getCurrentUserId(state)) return

    dispatch(createNewChat())
  }
}

export function createNewChat(messages: Array<Partial<App.ChatMessage>> = EmptyArray): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const regionCode = state.geo.currentRegionCode
    const locale = state.geo.locale
    const customerId = getCurrentUserId(state)
    const account = state.auth.account
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const hasLoyaltyPrioritySupport = getIsLoyaltyPrioritySupportNumberEnabled(state)
    const loyaltyAccountTier = getLoyaltyAccountTier(state)
    const orderBookingNumbers = getOrderBookingNumbers(state)
    const assistantVersion = getSupportAssistantVersion(state)

    dispatch({
      type: API_CALL,
      api: CREATE_NEW_CHAT,
      request: async() => {
        const [newChat, newMessages] = await createChat(regionCode, customerId, account.givenName, canViewLuxPlusBenefits, hasLoyaltyPrioritySupport, loyaltyAccountTier, orderBookingNumbers, undefined, locale, assistantVersion)

        messages.forEach(message => sendChatMessage({ ...message, chatId: newChat.id }))
        return {
          chat: newChat,
          messages: [...newMessages, ...(messages as Array<App.ChatMessage>)],
        }
      },
    })
  }
}

export function fetchChatWelcomeMessage(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const regionCode = state.geo.currentRegionCode
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const hasLoyaltyPrioritySupport = getIsLoyaltyPrioritySupportNumberEnabled(state)
    const loyaltyAccountTier = getLoyaltyAccountTier(state)

    return dispatch({
      type: API_CALL,
      api: FETCH_CHAT_WELCOME_MESSAGE,
      request: () => getChatWelcomeMessage(regionCode, canViewLuxPlusBenefits, hasLoyaltyPrioritySupport, loyaltyAccountTier),
    })
  }
}

export function setCurrentChatId(chatId: string): AppAction {
  return {
    type: SET_CURRENT_CHAT_ID,
    chatId,
  }
}

export function setChatOutageMessage(error: string | undefined) {
  return {
    type: SET_CHAT_OUTAGE_MESSAGE,
    error,
  }
}

export function prepareChatAfterLogin(chatId: string | undefined, customerId: string): AppAction {
  return async(dispatch, getState) => {
    if (chatId && chatId !== 'default') {
      try {
        const claimedChat = await putServerChat({ id: chatId, customerId })
        dispatch(updateChat(claimedChat))
        dispatch(setCurrentChatId(chatId))
        return chatId
      } catch {
        dispatch(setCurrentChatId('default'))
        return 'default'
      }
    }

    const currentChat = selectCurrentChat(getState())
    if (currentChat?.customerId === customerId) {
      return currentChat.id
    }

    dispatch(setCurrentChatId('default'))
    return 'default'
  }
}

export function fetchChatMessages(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const currentChat = selectCurrentChat(state)

    if (currentChat) {
      dispatch({
        type: API_CALL,
        api: FETCH_CHAT_MESSAGES,
        chatId: currentChat.id,
        request: async() => {
          const messages = await getChatMessages(currentChat.id)
          return {
            messages,
          }
        },
      })
    }
  }
}

export function postMessageFeedback(feedback: App.ChatMessageFeedback): AppAction {
  return {
    type: API_CALL,
    api: POST_MESSAGE_FEEDBACK,
    feedback,
    request: async() => {
      const intl = getCurrentIntl()
      const response = await postFeedback(feedback)

      if (response) {
        showSnackbar(
          intl.formatMessage({
            id: '6hmD3jyepwiqENV5Ms-_',
            defaultMessage: 'Thank you for your valuable feedback. It greatly helps us improve the user experience.',
            description: 'Snackbar message after successfully submitting chat feedback',
          }),
          'success',
          {
            heading: intl.formatMessage({
              id: 'HBuhfDRiVLY3Xcj3Z7Aa',
              defaultMessage: 'Feedback successfully submitted',
              description: 'Snackbar heading after successfully submitting chat feedback',
            }),
          },
        )
      }
      return response
    },
  }
}

export function addChatMessage(message: string, chatMessageMeta?: App.ChatMessageMeta, targetChatId?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const hasLoyaltyPrioritySupport = getIsLoyaltyPrioritySupportNumberEnabled(state)
    const loyaltyAccountTier = getLoyaltyAccountTier(state)
    const luxPlusContact = getLuxPlusSupportContactNumber(state)
    const loyaltyContact = getLoyaltySupportContactNumber(state)

    const currentChatId = targetChatId ?? state.supportAssistant.currentChatId ?? 'default'
    const customerId = getCurrentUserId(state)
    const account = state.auth.account
    const upcoming = getUpcomingOrders(state)
    const isCurrentOrder = upcoming.length === 1 && upcoming.every(isCurrentOrCloseToCheckIn)
    const orderBookingNumbers = getOrderBookingNumbers(state)
    const locale = state.geo?.locale
    const lesaNoAnswerAgentHandoffVariation = getSupportAssistantNoAnswerAgentHandoffVariation(state)
    const lesaBookingPickerVariation = getSupportAssistantBookingPickerVariation(state)
    const lesaEscalationVariation = getSupportAssistantAtiRemovalVariation(state)
    const ordersWithAirportTransfers = getOrdersWithAirportTransfers(state)
    const upcomingOrdersWithAirportTransfers = ordersWithAirportTransfers.filter(orderWithAirportTransfer => upcoming.find(upcomingOrder => upcomingOrder.id === orderWithAirportTransfer.id))

    const messageMetaPayload = {
      ...buildChatMessagePayload({
        chatMessageMeta,
        currentChatId,
        account,
        customerId,
        hasUpcomingBooking: !!upcoming.length,
        canViewLuxPlusBenefits,
        hasLoyaltyPrioritySupport,
        loyaltyAccountTier,
        luxPlusContact,
        loyaltyContact,
        message,
        locale,
        isCurrentOrder,
        orderBookingNumbers,
        ordersWithAirportTransfers,
        upcomingOrdersWithAirportTransfers,
        lesaNoAnswerAgentHandoffVariation,
        lesaBookingPickerVariation,
        lesaEscalationVariation,
      }),
      id: uuidv4(),
    } as App.ChatMessage

    if (currentChatId === 'default') {
      dispatch(createNewChat([messageMetaPayload]))
    } else {
      sendChatMessage(messageMetaPayload)
    }

    dispatch({
      type: ADD_CHAT_MESSAGE,
      message: messageMetaPayload,
      chatId: currentChatId,
    })

    // trigger customer portal actions for certain chat actions.  I.e. create Genesys chat.
    runClientChatAction(chatMessageMeta?.action, messageMetaPayload.meta?.payload)
  }
}

export function addChatResponse(message: App.ChatMessage, callback = (_response: { ok: boolean; id: string, duplicate: boolean }): void => {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    // block the duplicate welcome message coming through.
    const messageAlreadyExists = state.supportAssistant.messages[message.chatId!]?.some(mess => mess.id === message.id)

    if (callback) {
      callback({ ok: true, id: message.id, duplicate: !!messageAlreadyExists })
    }

    if (messageAlreadyExists) {
      return
    }

    if (isDefaultNoAnswerResponse(message.meta?.payload)) {
      dispatch(activateOptimizelyExperiment(Experiments.lesaNoAnswerAgentHandoff.id))
    }

    // ATI-removal A/B impression: a bot message offering a human. Control shows the agent pill,
    // treatment shows the escalation or callback pill. Menus carry these keys too, so some
    // non-dead-end surfaces activate as well — symmetrically across arms, so Optimizely's
    // numbers are diluted rather than biased; the primary readout is server-side.
    if (offersEscalation(message.meta)) {
      dispatch(activateOptimizelyExperiment(Experiments.lesaAtiRemoval.id))
    }

    // Booking-picker A/B impression: a bot message rendering either picker (pills in control,
    // the order-list component in treatment). Both triggers fire on some non-ask surfaces too
    // (other pill steps, the deterministic booking list), but symmetrically across arms, so
    // Optimizely's numbers are diluted rather than biased; the primary readout is server-side.
    const rendersBookingPicker =
      !!message.meta?.agentPills?.length ||
      !!message.meta?.content?.some(content => content.type === 'list-orders')
    if (rendersBookingPicker) {
      dispatch(activateOptimizelyExperiment(Experiments.lesaBookingPickerComponent.id))
    }

    dispatch({
      type: ADD_CHAT_MESSAGE,
      message,
      chatId: message.chatId!,
    })

    // Only resend may auto-run from the assistant's own response — a bot message must never trigger an arbitrary action.
    if (message.meta?.action === ChatActions.RESEND_CONFIRMATION_EMAIL) {
      runClientChatAction(message.meta.action, message.meta.payload)
    }
  }
}

export function updateChatTitle(chatTitle: App.ChatTitle): AppAction {
  return {
    type: UPDATE_CHAT_TITLE,
    chatId: chatTitle.chatId,
    title: chatTitle.title,
  }
}

export function updateChat(chat: App.Chat): AppAction {
  return {
    type: UPDATE_CHAT,
    chat,
  }
}

export function setUnreadCount(count: number): AppAction {
  return {
    type: SET_UNREAD_MESSAGE_COUNT,
    count,
  }
}

export function setLastUpdatedAt(lastUpdatedAt: string): AppAction {
  return {
    type: SET_LAST_UPDATED_AT,
    lastUpdatedAt,
  }
}

export function setChatMessages(chatId: string, messages: Array<App.ChatMessage>): AppAction {
  return {
    type: SET_CHAT_MESSAGES,
    chatId,
    messages,
  }
}
